import { memo, useCallback, useMemo } from 'react'

import type {
  VaultAllCategoriesInfo,
  VaultCategory as VaultCategoryType,
} from '@api'
import { useUnit } from 'effector-react'

import {
  $categoryQueryParam,
  $selectedCategory,
} from '@/models/vault/categories.ts'
import { MEDIA_TYPES } from '@/modules/vault/ui/category/constants.ts'
import { classNames } from '@/utils/helpers.ts'

import { VaultCategoryMediasPreview } from './vault-category-medias-preview'

interface Props {
  category: VaultCategoryType | VaultAllCategoriesInfo
  noSelection?: boolean
  className?: string
  onClick?: (category: VaultCategoryType) => void
}

export const VaultCategory = memo(
  ({ category, noSelection, className, onClick }: Props) => {
    const selectedCategory = useUnit($selectedCategory)
    const categoryQueryParam = useUnit($categoryQueryParam)
    const id = (category as VaultCategoryType)?.id || null
    const name = (category as VaultCategoryType)?.name || 'All Media'

    const isSelected = id ? selectedCategory?.id === id : !selectedCategory
    const filteredTypes = useMemo(
      () => MEDIA_TYPES.filter((type) => category?.[type.key]),
      [category],
    )

    const handleClick = useCallback(() => {
      // @ts-ignore
      onClick?.(category!)
    }, [category, onClick])

    if (!id && categoryQueryParam) {
      return null
    }

    return (
      <div
        onClick={handleClick}
        className={classNames(
          'group flex cursor-pointer items-center border-b py-1 transition duration-200 last:border-none',
          className,
        )}
      >
        <div
          className={classNames(
            'flex h-[64px] w-full items-center justify-between gap-1 rounded-lg px-3 py-2 transition duration-200',
            {
              'group-hover:bg-secondary-100 group-hover:dark:bg-secondary-800':
                !noSelection && !isSelected,
              'lg:bg-primary-500': !noSelection && isSelected,
              'cursor-pointer': !noSelection,
            },
          )}
        >
          <div className="flex flex-col">
            <p
              className={classNames(
                'text-text-default text-sm max-w-[200px] truncate',
                {
                  'lg:text-white': isSelected,
                },
              )}
            >
              {name}
            </p>
            {category && filteredTypes.length === 0 && (
              <span
                className={classNames(
                  'text-secondary-300 mt-2 text-xs font-medium transition',
                  {
                    'lg:text-white': isSelected,
                  },
                )}
              >
                Empty
              </span>
            )}
            {!!category && filteredTypes.length > 0 && (
              <div className="mt-2 flex flex-wrap sm:items-center">
                {filteredTypes.map((type, i) => (
                  <div
                    key={type.key}
                    className="flex items-center"
                  >
                    <type.icon
                      className={classNames('stroke-secondary-300 mr-1', {
                        'lg:stroke-white': isSelected,
                      })}
                    />
                    <span
                      className={classNames(
                        'text-secondary-300 text-xs font-medium',
                        { 'lg:text-white': isSelected },
                      )}
                    >
                      {category[type.key] as number}
                    </span>
                    {i !== filteredTypes.length - 1 && (
                      <div
                        className={classNames(
                          'bg-secondary-300 dark:bg-secondary-400 mx-2 hidden h-2 w-2 rounded-full opacity-30 md:block',
                          {
                            'bg-white opacity-70 dark:bg-white': isSelected,
                          },
                        )}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div
            className={classNames('flex', {
              '-ml-[20px]':
                category?.medias?.length && category?.medias?.length > 1,
            })}
          >
            <VaultCategoryMediasPreview medias={category?.medias} />
          </div>
        </div>
      </div>
    )
  },
)

VaultCategory.displayName = 'VaultCategory'
